import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { useTranslation, Trans } from 'react-i18next';
import Img from 'gatsby-image/withIEPolyfill';

import {
  truncateText,
  sortBy,
  uniq,
  flatten,
  mapBy,
  getReferenceUrl,
  convertRToSuperscript,
} from '../utils/utils';
import { LinkReference } from '../graphql-fragments/LinkReference';
import SearchBox, { MatchField } from './SearchBox';
import WidgetSection from './WidgetSection';
import { Contact } from './ContactBlock';
import ReadMoreLink from './ReadMoreLink';
import GenericLink from './GenericLink';

import * as styles from './OurProductsHighlightsSection.module.scss';

import greenCircles from '../assets/images/circles/circlelines_green.png';
import { HIDDEN_FEATURES } from '../constants';
import { GatsbySanityImageFluidNoBase64 } from '../types';

type ID = string;

interface Product {
  _id: ID;
  unlisted?: boolean;
  name: string;
  slug: {
    current: string;
  };
  type: {
    name: string;
  };
  activeSubstanceInfos?: Array<{
    activeSubstance: {
      name: string;
    };
  }>;
  applications?: Array<{
    crop: {
      name: string;
    };
    cropApplications?: Array<{
      problem: {
        name: string;
      };
    }>;
  }>;
}

interface Delegate {
  _id: ID;
  contact: Contact;
  regions: Array<{
    _id: ID;
  }>;
}

interface Company {
  _id: ID;
  contact: Contact;
  location: {
    _id: ID;
  };
}

interface Region {
  _id: ID;
  name: string;
  slug: {
    current: string;
  };
}

interface Location {
  _id: ID;
  name: string;
  region: {
    _id: ID;
  };
  slug: {
    current: string;
  };
}

interface QueryData {
  sanityHomepage: {
    ourProductsHighlights: Array<{
      _key: string;
      title: string;
      readMoreUrl: string;
      readMoreReference: LinkReference;
      body: string;
      image: {
        asset: {
          fluid: GatsbySanityImageFluidNoBase64;
        };
      };
    }>;
  };
  allSanityProduct: {
    nodes: Array<Product>;
  };
  allSanityDistributorDelegate: {
    nodes: Array<Delegate>;
  };
  allSanityDistributorCompany: {
    nodes: Array<Company>;
  };
  allSanityDistributorLocation: {
    nodes: Array<Location>;
  };
  allSanityDistributorRegion: {
    nodes: Array<Region>;
  };
}

const OurProductsHighlightsSection = (): React.ReactElement => {
  const { t } = useTranslation();
  const data = useStaticQuery<QueryData>(
    graphql`
      {
        sanityHomepage {
          ourProductsHighlights {
            _key
            title
            readMoreUrl
            readMoreReference {
              ...LinkReference
            }
            body
            image {
              asset {
                fluid(maxWidth: 760, maxHeight: 420) {
                  ...GatsbySanityImageFluid_noBase64
                }
              }
            }
          }
        }
        allSanityProduct(sort: { fields: name }) {
          nodes {
            _id
            unlisted
            name
            slug {
              current
            }
            type {
              name
            }
            activeSubstanceInfos {
              activeSubstance {
                name
              }
            }
            applications {
              crop {
                name
              }
              cropApplications {
                problem {
                  name
                }
              }
            }
          }
        }
        allSanityDistributorDelegate {
          nodes {
            _id
            contact {
              ...Contact
            }
            regions {
              _id
            }
          }
        }
        allSanityDistributorCompany {
          nodes {
            _id
            contact {
              ...Contact
            }
            location {
              _id
            }
          }
        }
        allSanityDistributorLocation(sort: { fields: name }) {
          nodes {
            _id
            name
            region {
              _id
            }
            slug {
              current
            }
          }
        }
        allSanityDistributorRegion(sort: { fields: name }) {
          nodes {
            _id
            name
            slug {
              current
            }
          }
        }
      }
    `,
  );

  const {
    allSanityProduct: { nodes: allProducts },
    allSanityDistributorLocation: { nodes: allLocations },
    allSanityDistributorRegion: { nodes: allRegions },
    allSanityDistributorDelegate: { nodes: allDelegates },
    allSanityDistributorCompany: { nodes: allCompanies },
  } = data;

  const { ourProductsHighlights } = data.sanityHomepage || {};

  const regionsById = mapBy(allRegions, '_id');
  const locationsById = mapBy(allLocations, '_id');

  const usedRegions = sortBy(
    uniq([
      ...allLocations.map(location => location.region._id),
      ...flatten(allDelegates.map(delegate => delegate.regions.map(region => region._id))),
    ]).map(regionId => regionsById[regionId] as Region),
    'name',
  );
  const usedLocations = sortBy(
    uniq(allCompanies.map(company => company.location._id)).map(
      locationId => locationsById[locationId] as Location,
    ),
    'name',
  );

  return (
    <div className={styles.section}>
      <div className={styles.container}>
        <img className={styles.greenCirclesBackground} src={greenCircles} alt="" />
        <div className={styles.leftSide}>
          {(!HIDDEN_FEATURES.includes('products') ||
            !HIDDEN_FEATURES.includes('distributors_homepage_search')) && (
            <SearchBox<'product' | 'distributor', Product | Region | Location>
              withTypoCorrection={{ product: true }}
              placeholderLong={{
                product: t(
                  'product_page.search_box.placeholder_long',
                  'Search products by name, active substance, problem, crop or type',
                ) as string,
              }}
              minSearchTextChars={{
                product: 3,
              }}
              className={styles.searchBox}
              withBorderGreen
              typeOptions={{
                label: t('homepage.search_box.label', 'I want to search'),
                radios: [
                  ...(!HIDDEN_FEATURES.includes('products')
                    ? ([
                        {
                          inputValue: 'product',
                          label: t('homepage.search_box.product_radio_label', 'product'),
                        },
                      ] as const)
                    : []),
                  ...(!HIDDEN_FEATURES.includes('distributors_homepage_search')
                    ? ([
                        {
                          inputValue: 'distributor',
                          label: t('homepage.search_box.distributor_radio_label', 'distributor'),
                        },
                      ] as const)
                    : []),
                ],
              }}
              entriesGroups={[
                ...(!HIDDEN_FEATURES.includes('products')
                  ? ([
                      {
                        type: 'product',
                        matchFieldConfigs: {
                          activeSubtance: t(
                            'product_page.search_box.match_location.active_substance',
                            'Active Substance',
                          ),
                          problem: t('product_page.search_box.match_location.problem', 'Problem'),
                          crop: t('product_page.search_box.match_location.crop', 'Crop'),
                          type: t('product_page.search_box.match_location.type', 'Product type'),
                        },
                        entries: sortBy(allProducts, 'name')
                          .filter(product => !product.unlisted)
                          .map(product => ({
                            name: product.name,
                            url: '/products/' + product.slug.current,
                            matchFields: [
                              {
                                name: 'activeSubtance',
                                values: product.activeSubstanceInfos?.map(
                                  ({ activeSubstance }) => activeSubstance.name,
                                ),
                              },
                              {
                                name: 'problem',
                                values: uniq(
                                  flatten(
                                    product.applications
                                      ?.map(({ cropApplications }) => cropApplications)
                                      .filter(
                                        (
                                          ca,
                                        ): ca is {
                                          problem: {
                                            _id: ID;
                                            name: string;
                                          };
                                        }[] => !!ca,
                                      ) || [],
                                  )
                                    .map(cropApplication => cropApplication.problem.name)
                                    .filter(Boolean),
                                ),
                              },
                              {
                                name: 'crop',
                                values: product.applications
                                  ?.map(({ crop }) => crop.name)
                                  .filter(Boolean),
                              },
                              {
                                name: 'type',
                                values: [product.type.name],
                              },
                            ].filter(
                              (matchField): matchField is MatchField =>
                                !!matchField.values && matchField.values.length > 0,
                            ),
                          })),
                      },
                    ] as const)
                  : []),
                ...(!HIDDEN_FEATURES.includes('distributors_homepage_search')
                  ? ([
                      {
                        type: 'distributor',
                        title: 'Regions',
                        entries: usedRegions.map(region => ({
                          name: region.name,
                          url: '/distributors/?region=' + region.slug.current,
                        })),
                      },
                      {
                        type: 'distributor',
                        title: 'Locations',
                        entries: usedLocations.map(location => ({
                          name: location.name,
                          url: '/distributors/?location=' + location.slug.current,
                        })),
                      },
                    ] as const)
                  : []),
              ]}
              noResultsMessage={{
                product: (
                  <>
                    <span>
                      {t(
                        'products_page.search_box.no_search_results_text',
                        'No products match the searched keyword.',
                      )}
                    </span>
                    <span className={styles.noSearchResultsHint}>
                      <Trans
                        i18nKey="homepage.search_box.no_products_search_results_hint"
                        defaults="Try searching for your product using our advanced products filters <0>here</0>."
                        components={[
                          <GenericLink key="link" to="/products/">
                            _
                          </GenericLink>,
                        ]}
                      />
                    </span>
                  </>
                ),
                distributor: (
                  <>
                    <span>
                      {t(
                        'distributors_page.search_box.no_search_results_text',
                        'No distributor regions or locations match the searched keyword.',
                      )}
                    </span>
                    <span className={styles.noSearchResultsHint}>
                      <Trans
                        i18nKey="homepage.search_box.no_distributors_search_results_hint"
                        defaults="Try searching for your region or location using our advanced distributor filters <0>here</0>."
                        components={[
                          <GenericLink key="link" to="/distributors/">
                            _
                          </GenericLink>,
                        ]}
                      />
                    </span>
                  </>
                ),
              }}
              renderNamePart={convertRToSuperscript}
            ></SearchBox>
          )}

          <h2 className={styles.sectionTitle}>{t('our_products_section.title', 'Our products')}</h2>
          <div className={styles.content}>
            {ourProductsHighlights?.map((highlight, i) => (
              <GenericLink
                noStyle
                key={highlight._key}
                to={
                  highlight.readMoreReference
                    ? getReferenceUrl(
                        highlight.readMoreReference.__typename,
                        highlight.readMoreReference.slug.current,
                      )
                    : highlight.readMoreUrl
                }
                className={i !== ourProductsHighlights.length - 1 ? styles.card : styles.lastCard}
              >
                <Img
                  className={styles.cardImage}
                  loading="eager"
                  fadeIn={false}
                  fluid={highlight.image.asset.fluid}
                  alt={highlight.title}
                />
                <div className={styles.cardTextContainer}>
                  <span className={styles.title} title={highlight.title}>
                    {convertRToSuperscript(truncateText(highlight.title, 70))}
                  </span>
                  <p className={styles.paragraph}>
                    {convertRToSuperscript(truncateText(highlight.body, 190))}
                  </p>
                  <ReadMoreLink styleOnly>
                    {t('our_products_section.go_to_products_link', 'Go to products')}
                  </ReadMoreLink>
                </div>
              </GenericLink>
            ))}
          </div>
        </div>
        <div className={styles.rightSide}>
          <WidgetSection></WidgetSection>
        </div>
      </div>
    </div>
  );
};

export default OurProductsHighlightsSection;
